// lib/client/gtm.js
'use client';

const DATA_LAYER_KEY = 'dataLayer';

function getDataLayer() {
  if (typeof window === 'undefined') return null;
  if (!window[DATA_LAYER_KEY]) {
    window[DATA_LAYER_KEY] = [];
  }
  return window[DATA_LAYER_KEY];
}

function cleanParams(params) {
  return Object.fromEntries(
    Object.entries(params).filter(
      // Keep explicit nulls so callers can clear stale values between events in GTM.
      ([, value]) => value !== undefined && value !== ''
    )
  );
}

export function pushGtmEvent(eventName, params = {}) {
  if (!eventName) return;
  const dataLayer = getDataLayer();
  if (!dataLayer) return;
  dataLayer.push({ event: eventName, ...cleanParams(params) });
}

export function trackHeartClick({ heartId, heartName, heartSlug, source, view }) {
  pushGtmEvent('heart_click', {
    heart_id: heartId,
    heart_name: heartName,
    heart_slug: heartSlug,
    // Clear any stale promotion fields so "one GA4 tag for all events" doesn't
    // accidentally attach previous promotion data to heart events (and vice versa).
    promotion_id: null,
    promotion_name: null,
    promotion_vendor: null,
    promotion_url: null,
    source,
    view,
  });
}

export function trackPromotionClick({
  promotionId,
  promotionName,
  promotionVendor,
  promotionUrl,
  source,
  view,
}) {
  pushGtmEvent('promotion_click', {
    // Clear any stale heart fields so "one GA4 tag for all events" doesn't
    // accidentally attach previous heart data to promotion events (and vice versa).
    heart_id: null,
    heart_name: null,
    heart_slug: null,
    promotion_id: promotionId,
    promotion_name: promotionName,
    promotion_vendor: promotionVendor,
    promotion_url: promotionUrl,
    source,
    view,
  });
}

export function trackWishlistSavedClick({ heart_name }) {
  pushGtmEvent('save_heart_wishlist', {
    heart_name,
    timestamp: new Date().toISOString(),
  });
}

export function trackExploreEntityClick({
  entityType,
  entityId,
  entityName,
  entitySlug,
  promotionVendor,
  promotionUrl,
  source,
  view,
}) {
  const normalizedType = String(entityType || '').toLowerCase();

  if (normalizedType === 'heart') {
    trackHeartClick({
      heartId: entityId,
      heartName: entityName,
      heartSlug: entitySlug,
      source,
      view,
    });
    return;
  }

  if (normalizedType === 'promotion') {
    trackPromotionClick({
      promotionId: entityId,
      promotionName: entityName,
      promotionVendor,
      promotionUrl,
      source,
      view,
    });
    return;
  }

  if (!normalizedType) return;

  pushGtmEvent(`${normalizedType}_click`, {
    // Keep these nulled for non-heart/promotion events so shared GTM variables
    // never inherit stale values from previous interactions.
    heart_id: null,
    heart_name: null,
    heart_slug: null,
    promotion_id: null,
    promotion_name: null,
    promotion_vendor: null,
    promotion_url: null,
    entity_type: normalizedType,
    entity_id: entityId,
    entity_name: entityName,
    entity_slug: entitySlug,
    source,
    view,
  });
}
